import React, { useState, useEffect } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate, useSearchParams } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import {
  assignTerritoriesToEmployees,
  getLmdTerritoryDetails,
  updateLmdTerritoryEmployees,
  getEmployeeDetails,
} from "../api/territory";
import { getAllLmdTerritories } from "../../../api/territoryMap";
import { getGlobalFilter } from "../../../api/filters";
import Dropdown from "../components/Dropdown";
import Popup from "../../../components/AuthPopup";
import Loader from "../../../components/Loader";
import { dd_supervisor, delivery_man, preseller, salesman } from "../../../utility/constant";

const SkeletonLoader = () => (
  <div className="relative overflow-hidden rounded-lg h-10 w-full bg-gray-200">
    <div className="absolute inset-0 animate-skeleton-shimmer bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
  </div>
);

const AddNewTerritory = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    emp_id: "",
    sales_office_id: "",
    territory_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [lmdTerritories, setLmdTerritories] = useState<any[]>([]);
  const [salesOffices, setSalesOffices] = useState<any[]>([]);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<any[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const navigate = useNavigate();

  // Handle edit mode - pre-fill territory_id from query params and fetch existing data
  useEffect(() => {
    const territoryId = searchParams.get("territory_id");
    if (territoryId) {
      setIsEditMode(true);
      setFormData((prev) => ({
        ...prev,
        territory_id: territoryId,
      }));
      // Fetch existing territory details
      fetchTerritoryDetails(territoryId);
    }
  }, [searchParams]);

  const fetchGlobalFilters = async (salesOfficeId?: string) => {
    try {
      setLoadingFilters(true);
      const data: any = {
        endpoint: "activity-report",
        positions: [delivery_man],
        regions: ["Western", "Southern", "Central", "Northern", "Eastern"]
      };
      
      // If sales office is selected, filter employees by it
      if (salesOfficeId) {
        data["sales_office_ids"] = [salesOfficeId];
      }
      
      const response = await getGlobalFilter(data);
      
      // Set sales offices
      if (response?.sales_offices) {
        setSalesOffices(response.sales_offices);
      }
      
      // Set all employees
      // Note: When sales_office_ids is passed to the API, employees are already filtered server-side
      if (response?.employees) {
        setAllEmployees(response.employees);
        setFilteredEmployees(response.employees);
      } else {
        setAllEmployees([]);
        setFilteredEmployees([]);
      }
    } catch (error: any) {
      console.error("Failed to fetch global filters:", error);
      setSalesOffices([]);
      setAllEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchTerritoryDetails = async (territoryId: string) => {
    try {
      setLoadingData(true);
      const response = await getLmdTerritoryDetails(territoryId);
      
      // Handle different response structures
      let territories = [];
      if (Array.isArray(response)) {
        territories = response;
      } else if (response?.items && Array.isArray(response.items)) {
        territories = response.items;
      } else if (response?.data && Array.isArray(response.data)) {
        territories = response.data;
      } else if (response && !Array.isArray(response)) {
        // Single territory object
        territories = [response];
      }
      
      if (territories.length > 0) {
        // Find the territory with matching territory_id
        const territory = territories.find((t: any) => 
          t.territory_id?.toString() === territoryId || 
          t.territory_id === territoryId
        );
        
        if (territory) {
          // Pre-populate form with existing data
          if (territory.assigned_employees && territory.assigned_employees.length > 0) {
            const firstEmp = territory.assigned_employees[0];
            const empId = firstEmp.emp_id?.toString() || firstEmp.emp_id;
            const salesOfficeId = firstEmp.sales_office_id?.toString() || firstEmp.sales_office_id;
            
            setFormData((prev) => ({
              ...prev,
              emp_id: empId || "",
              sales_office_id: salesOfficeId || "",
            }));
            
            // Fetch filters to populate dropdowns, then set the selected values
            if (salesOfficeId) {
              await fetchGlobalFilters(salesOfficeId);
            } else {
              await fetchGlobalFilters();
            }
          }
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch territory details:", error);
      if (error?.response?.status === 404) {
        showErrorModal("Territory details not found");
      } else if (error?.response?.status === 500) {
        showErrorModal("Error while fetching territory details");
      } else {
        const detail = error?.response?.data?.detail;
        showDetailErrors(detail, "An unexpected error occurred while fetching territory details.");
      }
    } finally {
      setLoadingData(false);
    }
  };

  const showErrorModal = (message: string) => {
    setPopupMessage(message || "An unexpected error occurred.");
    setShowPopup(true);
  };

  const showDetailErrors = (detail: any, fallback?: string) => {
    if (Array.isArray(detail) && detail.length > 0) {
      const combined = detail
        .map((err: any) => err?.msg || err?.message || String(err))
        .join("\n");
      showErrorModal(combined);
      return;
    }
    showErrorModal(detail?.msg || detail || fallback || "An unexpected error occurred.");
  };
  const handleChangeDropdown = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      return updated;
    });
  };
  
  const getLabelClass = (disabled: boolean) =>
    `block font-semibold mb-1 ${disabled ? "text-gray-400" : "text-gray-700"}`;
  
  const territoryOptions = lmdTerritories.map((territory) => ({
    value: territory.territory_id?.toString() || "",
    name: territory.territory_name || `Territory ${territory.territory_id}`,
  }));
  
  const salesOfficeOptions = salesOffices.map((office: any) => ({
    value: office.id?.toString() || office.sales_office_id?.toString() || "",
    name: office.name +" (" + office.id + ")",
  }));
  
  const employeeOptions = filteredEmployees.map((emp: any) => ({
    value: emp.id?.toString() || emp.emp_id?.toString() || "",
    name: emp.name_eng +" (" + emp.id + ")",
  }));
  
  const isTerritoryDisabled = territoryOptions.length === 0;
  
  const handleChange = (e: { target: { name: string; value: string | string[] } }) => {
    const { name, value } = e.target;
    // Dropdown returns string for single select
    const stringValue = Array.isArray(value) ? value[0] : value;
    
    if (name === "sales_office_id") {
      // When sales office changes, filter employees and clear employee selection
      setFormData({ ...formData, sales_office_id: stringValue, emp_id: "" });
      setEmployeeDetails(null);
      if (stringValue) {
        fetchGlobalFilters(stringValue);
      } else {
        fetchGlobalFilters();
      }
    } else if (name === "emp_id") {
      setFormData({ ...formData, [name]: stringValue });
    } else {
      setFormData({ ...formData, [name]: stringValue });
    }
  };

  // Fetch employee details when employee is selected
  useEffect(() => {
    const fetchEmpDetails = async () => {
      if (!formData.emp_id) {
        setEmployeeDetails(null);
        return;
      }

      try {
        setLoadingEmployeeDetails(true);
        const details = await getEmployeeDetails(formData.emp_id);
        setEmployeeDetails(details);
      } catch (error: any) {
        console.error("Failed to fetch employee details:", error);
        setEmployeeDetails(null);
      } finally {
        setLoadingEmployeeDetails(false);
      }
    };

    fetchEmpDetails();
  }, [formData.emp_id]);

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      
      // Validate that employee is selected
      if (!formData.emp_id) {
        setInvalidFields(["emp_id"]);
        showErrorModal("Please select an employee.");
        return;
      }

      // Validate territory_id is provided
      if (!formData.territory_id) {
        setInvalidFields(["territory_id"]);
        showErrorModal("Please select a territory.");
        return;
      }

      setInvalidFields([]);

      let response;
      const basePayload = {
        territory_id: formData.territory_id,
      };

      // Use PUT endpoints for edit mode, POST endpoints for create mode
      if (isEditMode) {
        // Update mode - use PUT endpoints, only send employee ID
          const payload = {
          emp_ids: [formData.emp_id],
          };
          response = await updateLmdTerritoryEmployees(formData.territory_id, payload);
      } else {
        // Create mode - use POST endpoints, only send employee ID
          const payload = {
            ...basePayload,
          emp_ids: [formData.emp_id],
          };
          response = await assignTerritoriesToEmployees(payload);
      }

      if (!response) {
        showErrorModal("No data received or malformed response");
        console.warn("No data received or malformed response:", response);
        return;
      }
      toast.success(response?.message);
      setTimeout(() => {
        navigate(URL_MAPPING.LMDTERRITORY);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to assign territory:", error);
      if (error?.response?.status === 404) {
        showErrorModal("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        showErrorModal(
          error?.response?.data?.detail || "Error while assigning the territory"
        );
      } else {
        const detail = error?.response?.data?.detail;
        showDetailErrors(detail);
      }
    } finally {
      setLoading(false);
    }
  };
  const fetchLmdTerritories = async (salesOfficeId?: string) => {
      try {
      const res = await getAllLmdTerritories(salesOfficeId, true); // Pass active_status: true
        if (!res || !Array.isArray(res)) {
          console.warn("Failed to load LMD territories");
          setLmdTerritories([]);
          return;
        }
        setLmdTerritories(res);
      } catch (error: any) {
        console.error("Failed to fetch LMD territories:", error);
        if (error?.response?.status === 404) {
          showErrorModal("LMD territories not found, Please try again later");
        } else if (error?.response?.status === 500) {
          showErrorModal("Error while fetching LMD territories");
        } else {
          const detail = error?.response?.data?.detail;
          showDetailErrors(detail, "An unexpected error occurred while fetching territories.");
        }
        setLmdTerritories([]);
      }
    };

  useEffect(() => {
    fetchLmdTerritories();
    // Fetch initial global filters (sales offices and employees)
    fetchGlobalFilters();
  }, []);

  // Refetch territories when sales office changes
  useEffect(() => {
    if (formData.sales_office_id) {
      fetchLmdTerritories(formData.sales_office_id);
    } else {
      fetchLmdTerritories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.sales_office_id]);
  return (
    <LayerAdmin>
      <Popup
        isOpen={showPopup}
        title="Error"
        setShowPopup={setShowPopup}
      >
        <p className="text-sm text-gray-600 whitespace-pre-line">
          {popupMessage}
        </p>
      </Popup>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            version="1.1"
            width="10"
            height="10"
            viewBox="0 0 256 256"
            xmlSpace="preserve"
          >
            <g
              style={{
                stroke: "none",
                strokeWidth: 0,
                strokeDasharray: "none",
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeMiterlimit: 10,
                fill: "none",
                fillRule: "nonzero",
                opacity: 1,
              }}
              transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
            >
              <polygon
                points="20.48,45 59.75,90 69.52,81.48 37.69,45 69.52,8.52 59.75,0 "
                style={{
                  stroke: "none",
                  strokeWidth: 1,
                  strokeDasharray: "none",
                  strokeLinecap: "butt",
                  strokeLinejoin: "miter",
                  strokeMiterlimit: 10,
                  fill: "rgb(0,0,0)",
                  fillRule: "nonzero",
                  opacity: 1,
                }}
                transform="  matrix(1 0 0 1 0 0) "
              />
            </g>
          </svg>
        </button>
        <h3 className="text-xl font-bold text-gray-700">
          {isEditMode ? "Edit Territory Assignment" : "Assign Territory"}
        </h3>
      </div>
      {loadingData ? (
        <div className="flex justify-center items-center p-8">
          <Loader />
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="p-4 lg:w-1/3 sm:w-full">
        <div className="my-2">
          <label className={getLabelClass(loadingFilters)}>
            Sales Office *
          </label>
          {loadingFilters ? (
            <SkeletonLoader />
          ) : (
            <Dropdown
              name="sales_office_id"
              value={formData.sales_office_id}
              onChange={handleChange}
              options={salesOfficeOptions}
              className={getInputClass("sales_office_id")}
              placeholder="Select Sales Office"
              searchable={true}
              disabled={loadingFilters}
            />
          )}
        </div>
        <div className="my-2">
          <label className={getLabelClass(loadingFilters || !formData.sales_office_id)}>
            Employee *
          </label>
          {loadingFilters ? (
            <SkeletonLoader />
          ) : (
            <Dropdown
              name="emp_id"
              value={formData.emp_id}
              onChange={handleChange}
              options={employeeOptions}
              className={getInputClass("emp_id")}
              placeholder={formData.sales_office_id ? "Select Employee" : "Select Sales Office first"}
              searchable={true}
              disabled={loadingFilters || !formData.sales_office_id}
            />
          )}
          {!formData.sales_office_id && (
            <p className="text-xs text-gray-500 mt-1">
              Please select a sales office first to filter employees
            </p>
          )}
          {loadingEmployeeDetails && (
            <div className="mt-2 flex items-center gap-2">
              <Loader />
              <span className="text-sm text-gray-600">Loading employee details...</span>
            </div>
          )}
          {!loadingEmployeeDetails && employeeDetails && formData.emp_id && (
            <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-700 mb-3">Employee Details:</h4>
                    <div className="space-y-2 text-sm">
                {employeeDetails.name_eng && (
                            <div>
                              <span className="font-medium text-gray-600">Name:</span>{" "}
                    <span className="text-gray-800">{employeeDetails.name_eng}</span>
                  </div>
                )}
                {employeeDetails.emp_id && (
                  <div>
                    <span className="font-medium text-gray-600">Employee ID:</span>{" "}
                    <span className="text-gray-800">{employeeDetails.emp_id}</span>
                            </div>
                          )}
                {employeeDetails.territories && Array.isArray(employeeDetails.territories) && employeeDetails.territories.length > 0 && (
                            <div>
                              <span className="font-medium text-gray-600 block mb-2">Assigned Territories:</span>
                              <div className="space-y-1">
                      {employeeDetails.territories.map((territory: any, idx: number) => (
                                  <div key={idx} className="text-gray-800 pl-4">
                                    • {territory.territory_name} ({territory.territory_id})
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                {employeeDetails.territories && Array.isArray(employeeDetails.territories) && employeeDetails.territories.length === 0 && (
                            <div className="text-gray-500 text-sm">
                              No territories assigned
                            </div>
                          )}
                        </div>
            </div>
          )}
        </div>
        <div className="my-2">
          <label className={getLabelClass(isTerritoryDisabled)}>
            Territory *
          </label>
          <Dropdown
            name="territory_id"
            value={formData.territory_id}
            onChange={handleChangeDropdown}
            options={territoryOptions}
            className={getInputClass("territory_id")}
            placeholder="Select Territory"
            searchable={true}
            disabled={isTerritoryDisabled || isEditMode}
          />
          {isEditMode && (
            <p className="text-xs text-gray-500 mt-1">
              Territory cannot be changed in edit mode
            </p>
          )}
        </div>

        
        <button
          type="submit"
          disabled={loading || loadingData}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isEditMode ? "Update" : "Assign"} {loading && <ButtonLoader />}
        </button>
      </form>
      )}
    </LayerAdmin>
  );
};

export default AddNewTerritory;
